import { useMemo } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import _ from "lodash"
import { Link } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-tourist/constant"
import { useTourist } from "extensions/em-tourist/hooks/useTourist"

import { getIcon } from "utils/icon"

import { EmptyData } from "components/EmptyData"
import { Head } from "components/Head"
import { ImageCard } from "components/ImageCard"
import { ShopIcon } from "components/icons"
import { MotionDiv } from "components/motions"
import { VenueSelector } from "components/VenueSelector"
import { useVenueController } from "hooks/venue/useVenueController"

export const EmTouristMobilePage = () => {
  const { t } = useTranslation()
  const { isMultiVenueProject, venues } = useVenue()
  const { extensionConfig } = useExtension()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const touristConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        locations: [],
      },
    [extensionConfig]
  )
  const touristLocations = _.get(touristConfig, "locations")

  const { filterByVenueId, filteredTouristByVenue } = useTourist(
    touristLocations,
    venueFilter
  )

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`EmTouristPage`] || {}

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.tourist) || <ShopIcon />,
    [theme]
  )

  const {
    root,
    touristImageCard,
    gridContainer,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  return (
    <>
      <Head title={t("Tourist")} />
      <MotionDiv
        sx={root}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        {filteredTouristByVenue?.length > 0 ? (
          <Box sx={gridContainer}>
            {filteredTouristByVenue?.map((data) => {
              const { name, location, image, level_name } = data || {}
              const levelText = t("Level Text", {
                level: t(level_name),
              })
              return (
                <ImageCard
                  key={data?.id}
                  sx={touristImageCard}
                  component={Link}
                  to={`/maps/place/${location?.id}`}
                  title={t(name)}
                  subheader={
                    <>
                      <Typography
                        className="location-text"
                        component="span"
                        variant="subtitle"
                      >
                        {levelText}
                      </Typography>
                    </>
                  }
                  image={image}
                  avatar={null}
                />
              )
            })}
          </Box>
        ) : (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("Coming soon")}
              secondary={t("Stay tuned for the update")}
            />
          </Box>
        )}
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={filterByVenueId}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </MotionDiv>
    </>
  )
}
